import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiError } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Field, TextArea, TextInput } from '@/components/ui/Field';
import { LoadingState } from '@/components/StateMessage/StateMessage';
import styles from './automations.module.css';

type AgentRow = {
  id: string;
  name: string;
  description: string | null;
  enabled: boolean;
  config: {
    system_prompt?: string;
    user_prompt_template?: string;
    max_tokens?: number;
  };
};

const DEFAULT_CONFIG = {
  system_prompt: 'Je bent een professionele sales-assistent. Antwoord in het Nederlands.',
  user_prompt_template: 'Lead: {{lead}}\nInbound: {{inbound}}',
  max_tokens: 800,
};

type Props = {
  projectId: string;
};

export function AgentsPanel({ projectId }: Props) {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: 'Reply agent',
    description: '',
    enabled: true,
    ...DEFAULT_CONFIG,
  });
  const [simulatePreview, setSimulatePreview] = useState<string | null>(null);

  const listQ = useQuery({
    queryKey: ['agents', projectId],
    queryFn: () => apiFetch<{ agents: AgentRow[] }>(`/api/agents?project_id=${projectId}`),
  });

  const saveM = useMutation({
    mutationFn: () => {
      const body = {
        project_id: projectId,
        name: form.name,
        description: form.description || undefined,
        enabled: form.enabled,
        config: {
          system_prompt: form.system_prompt,
          user_prompt_template: form.user_prompt_template,
          max_tokens: form.max_tokens,
        },
      };
      if (selectedId) {
        return apiFetch(`/api/agents/${selectedId}`, {
          method: 'PATCH',
          body: JSON.stringify({
            name: form.name,
            description: form.description || null,
            enabled: form.enabled,
            config: body.config,
          }),
        });
      }
      return apiFetch('/api/agents', { method: 'POST', body: JSON.stringify(body) });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['agents', projectId] });
    },
  });

  const simulateM = useMutation({
    mutationFn: () =>
      apiFetch<{ user_preview: string; system_prompt: string }>(`/api/agents/${selectedId}/simulate`, {
        method: 'POST',
        body: JSON.stringify({
          payload: { subject: 'Re: offerte', snippet: 'Bedankt, kunnen we volgende week bellen?' },
          context: { lead_id: '00000000-0000-4000-8000-000000000001' },
        }),
      }),
    onSuccess: (data) => {
      setSimulatePreview(`System:\n${data.system_prompt}\n\nUser:\n${data.user_preview}`);
    },
  });

  function loadAgent(a: AgentRow) {
    setSelectedId(a.id);
    setForm({
      name: a.name,
      description: a.description ?? '',
      enabled: a.enabled,
      system_prompt: a.config.system_prompt ?? DEFAULT_CONFIG.system_prompt,
      user_prompt_template: a.config.user_prompt_template ?? DEFAULT_CONFIG.user_prompt_template,
      max_tokens: a.config.max_tokens ?? 800,
    });
  }

  if (listQ.isLoading) return <LoadingState />;

  return (
    <div className={styles.workspace}>
      <aside className={styles.listPanel}>
        <h3>Agents</h3>
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            setSelectedId(null);
            setForm({ name: 'Nieuwe agent', description: '', enabled: true, ...DEFAULT_CONFIG });
          }}
        >
          + Agent
        </Button>
        {(listQ.data?.agents ?? []).map((a) => (
          <button
            key={a.id}
            type="button"
            className={[styles.listBtn, selectedId === a.id ? styles.listBtnActive : ''].join(' ')}
            onClick={() => loadAgent(a)}
          >
            {a.name} {!a.enabled ? '(uit)' : ''}
          </button>
        ))}
      </aside>
      <div className={styles.editorPanel}>
        <Field label="Naam">
          <TextInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Beschrijving">
          <TextInput value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </Field>
        <label>
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
          />{' '}
          Ingeschakeld
        </label>
        <Field label="System prompt">
          <TextArea
            rows={4}
            value={form.system_prompt}
            onChange={(e) => setForm({ ...form, system_prompt: e.target.value })}
          />
        </Field>
        <Field label="User template" hint="Placeholders: {{lead}}, {{inbound}}">
          <TextArea
            rows={3}
            value={form.user_prompt_template}
            onChange={(e) => setForm({ ...form, user_prompt_template: e.target.value })}
          />
        </Field>
        <Field label="Max tokens">
          <TextInput
            type="number"
            value={String(form.max_tokens)}
            onChange={(e) => setForm({ ...form, max_tokens: Number(e.target.value) || 800 })}
          />
        </Field>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <Button type="button" disabled={saveM.isPending} onClick={() => saveM.mutate()}>
            Opslaan
          </Button>
          {selectedId ? (
            <Button type="button" variant="secondary" disabled={simulateM.isPending} onClick={() => simulateM.mutate()}>
              Simulate prompt
            </Button>
          ) : null}
        </div>
        {saveM.isError && saveM.error instanceof ApiError ? <p>{saveM.error.message}</p> : null}
        {simulatePreview ? <pre className={styles.outputPre}>{simulatePreview}</pre> : null}
      </div>
    </div>
  );
}
